/**
 * TaskAlert — การ์ดเด้งเมื่อมีงานถึงตัว (อยู่ทุกหน้า เหมือนแชทลอย)
 *
 * ฟัง Socket.IO "task:event" จาก taskController — สั่งงานให้ · ถึงคิวขั้นตอนผลิต · ติดปัญหา · เสร็จ · คอมเมนต์
 * การ์ดค้างมุมจอจนกว่าจะกด (ไม่หายเองแบบแถบแจ้งเตือนธรรมดา) + เสียงติ๊ง + แจ้งเตือนบนจอคอม
 * ใช้เสียง/การตั้งค่าปิดเสียงร่วมกับแชท (components/chat/chatAlert.js)
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ClipboardList, X, AlertOctagon, CheckCircle2, MessageSquare, Play, ShieldCheck, Undo2 } from "lucide-react";
import socket from "../../socket";
import { playDing, desktopNotify } from "../chat/chatAlert";
import i18n from "../../i18n";
import { useLang } from "../../utils/useTx";

// 2 ภาษา TH/EN (23 ต.ค. 69) — T(ไทย, อังกฤษ) ตามภาษาปัจจุบัน · คอมโพเนนต์หลักเรียก useLang() ให้วาดใหม่ตอนสลับภาษา
const T = (th, en) => (i18n.language?.startsWith("en") ? en : th);

const LOOK = {
  TASK_ASSIGNED: { icon: ClipboardList, tone: "bg-slate-900 text-white", label: "งานใหม่", en: "New task" },
  TASK_READY: { icon: Play, tone: "bg-indigo-600 text-white", label: "ถึงคิวคุณแล้ว", en: "Your turn now" },
  TASK_BLOCKED: { icon: AlertOctagon, tone: "bg-rose-600 text-white", label: "งานติดปัญหา", en: "Task blocked" },
  TASK_DONE: { icon: CheckCircle2, tone: "bg-emerald-600 text-white", label: "งานเสร็จแล้ว", en: "Task done" },
  TASK_COMMENT: { icon: MessageSquare, tone: "bg-sky-600 text-white", label: "ข้อความในงาน", en: "Task message" },
  TASK_CHANGED: { icon: ClipboardList, tone: "bg-slate-700 text-white", label: "งานมีการเปลี่ยนแปลง", en: "Task changed" },
  // ตีกลับ (utils/taskPlan.js) · ขั้นรอหัวหน้าตรวจ (migration 092)
  TASK_REJECTED: { icon: Undo2, tone: "bg-rose-600 text-white", label: "งานถูกส่งกลับให้แก้", en: "Task sent back for fixes" },
  TASK_REVIEW: { icon: ShieldCheck, tone: "bg-violet-600 text-white", label: "มีงานรอคุณตรวจ", en: "A task needs your review" },
  TASK_APPROVED: { icon: ShieldCheck, tone: "bg-emerald-600 text-white", label: "ผ่านการตรวจแล้ว", en: "Approved" },
};

export default function TaskAlert() {
  useLang();
  const { userInfo } = useSelector((s) => s.auth);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [cards, setCards] = useState([]);

  useEffect(() => {
    if (!userInfo) return undefined;
    const on = (p) => {
      if (!p?.taskId) return;
      // กำลังเปิดงานนั้นอยู่แล้ว — หน้าจอรีเฟรชเอง ไม่ต้องเด้ง
      if (window.location.pathname === `/tasks/${p.taskId}`) return;
      const look = LOOK[p.type] || LOOK.TASK_CHANGED;
      setCards((cs) => [{ ...p, key: `${p.taskId}-${Date.now()}` }, ...cs.filter((c) => c.taskId !== p.taskId)].slice(0, 4));
      playDing();
      if (document.hidden) {
        desktopNotify({ title: T(look.label, look.en), body: p.message, onClick: () => navigate(`/tasks/${p.taskId}`) });
      }
    };
    socket.on("task:event", on);
    return () => socket.off("task:event", on);
  }, [userInfo, navigate]);

  // เปิดหน้างานนั้นแล้ว เก็บการ์ดของงานนั้นทิ้ง
  useEffect(() => {
    const m = pathname.match(/^\/tasks\/(\d+)$/);
    if (m) setCards((cs) => cs.filter((c) => String(c.taskId) !== m[1]));
  }, [pathname]);

  if (!userInfo || !cards.length) return null;

  return (
    <div className="fixed right-5 bottom-[calc(6rem+env(safe-area-inset-bottom))] z-[61] w-[min(22rem,calc(100vw-2.5rem))] space-y-2" aria-live="polite">
      {cards.map((c) => {
        const look = LOOK[c.type] || LOOK.TASK_CHANGED;
        const Icon = look.icon;
        return (
          <div key={c.key} className="rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden animate-[bounce_0.6s_ease-out_1] motion-reduce:animate-none">
            <button
              type="button"
              onClick={() => {
                setCards((cs) => cs.filter((x) => x.key !== c.key));
                navigate(`/tasks/${c.taskId}`);
              }}
              className="w-full text-left flex items-start gap-3 p-3 hover:bg-slate-50"
            >
              <span className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${look.tone}`}>
                <Icon className="w-4 h-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12px] font-black text-slate-900">{T(look.label, look.en)}</span>
                <span className="block text-[13px] text-slate-700 line-clamp-2">{c.message}</span>
                <span className="block mt-0.5 text-[11px] font-semibold text-indigo-600">{T("กดเพื่อเปิดงาน", "Tap to open")}</span>
              </span>
            </button>
            <div className="flex justify-end border-t border-slate-100">
              <button
                type="button"
                onClick={() => setCards((cs) => cs.filter((x) => x.key !== c.key))}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-slate-400 hover:text-slate-700"
              >
                <X className="w-3 h-3" /> {T("ปิด", "Close")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
